import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navigation from '@/components/Navigation';
import { FileText, AlertCircle, Shield, Scale, FileCheck } from 'lucide-react';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Terms of Service - AI Mastery Hub</title>
        <meta name="description" content="AI Mastery Hub Terms of Service - Digital Course Terms for our educational platform." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-block px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full text-sm mb-6">
            <span className="text-blue-400">📋 Legal Agreement</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
            <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Terms of Service
            </span>
          </h1>
          <p className="text-xl text-gray-400 mb-2">
            AI Mastery Hub - Digital Course Terms
          </p>
          <p className="text-lg text-gray-500">
            Last Updated: November 24, 2025
          </p>
          <p className="text-lg text-gray-500">
            Effective Date: November 24, 2025
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/20">

            {/* Important Notice */}
            <div className="bg-red-600/10 border border-red-500/30 rounded-xl p-6 mb-12">
              <h3 className="text-xl font-bold text-red-400 mb-3">⚠️ PLEASE READ CAREFULLY</h3>
              <p className="text-gray-300 text-sm leading-relaxed mb-3">
                BY COMPLETING A PURCHASE, CREATING AN ACCOUNT, OR ACCESSING ANY COURSE MATERIALS, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE IN THEIR ENTIRETY.
              </p>
              <p className="text-gray-300 text-sm leading-relaxed">
                IF YOU DO NOT AGREE TO THESE TERMS, DO NOT PURCHASE OR ACCESS ANY COURSES.
              </p>
            </div>

            {/* 1. DEFINITIONS */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <FileText className="w-8 h-8 text-purple-400" />
                <h2 className="text-3xl font-bold">1. Definitions</h2>
              </div>
              <div className="text-gray-300 leading-relaxed space-y-3">
                <p>Throughout these Terms of Service, the following definitions apply:</p>
                <p><strong className="text-white">"Company," "we," "us," or "our"</strong> refers to AI Mastery Hub, operated by Worldview Industries LLC, the sole proprietor and operator of this platform.</p>
                <p><strong className="text-white">"Platform"</strong> refers to our website, applications, learning management system, and all related digital infrastructure through which Courses are delivered.</p>
                <p><strong className="text-white">"Courses" or "Course Materials"</strong> refers to all digital educational content including but not limited to: video lessons, tutorials, written guides, downloadable resources, templates, prompts, code examples, exercises, quizzes, assessments, supplementary materials, and any other content provided as part of any course or bundle offering.</p>
                <p><strong className="text-white">"User," "you," or "your"</strong> refers to any individual or entity that accesses the Platform, creates an account, purchases a Course, or uses any Course Materials.</p>
                <p><strong className="text-white">"Purchase"</strong> refers to any completed transaction for Course access, including individual courses, bundles, or subscription-based access.</p>
                <p><strong className="text-white">"Intellectual Property"</strong> refers to all content, materials, trademarks, copyrights, trade secrets, and proprietary information owned by the Company.</p>
                <p><strong className="text-white">"Third-Party Tools"</strong> refers to external software, platforms, and services referenced or utilized within Courses, including but not limited to AI language models, APIs, software applications, and online services.</p>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* 2. ACCEPTANCE OF TERMS */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <FileCheck className="w-8 h-8 text-blue-400" />
                <h2 className="text-3xl font-bold">2. Acceptance of Terms</h2>
              </div>
              <div className="text-gray-300 leading-relaxed space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-purple-400 mb-3">2.1 BINDING AGREEMENT</h3>
                  <p className="mb-2">By taking any of the following actions, you enter into a legally binding agreement with the Company:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Clicking "I Agree," "Purchase," "Buy Now," or similar acceptance buttons during checkout</li>
                    <li>Completing any payment for Course access</li>
                    <li>Creating an account on the Platform</li>
                    <li>Accessing, viewing, downloading, or using any Course Materials</li>
                    <li>Continuing to use the Platform after these Terms have been updated</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-blue-400 mb-3">2.2 LEGAL CAPACITY</h3>
                  <p className="mb-2">You represent and warrant that:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>You are at least 18 years of age or the age of legal majority in your jurisdiction, whichever is greater</li>
                    <li>You have the legal capacity to enter into binding contracts</li>
                    <li>If acting on behalf of an organization, you have the authority to bind that organization to these Terms</li>
                    <li>You are not prohibited by law from using educational services or purchasing digital goods in your jurisdiction</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-cyan-400 mb-3">2.3 ACKNOWLEDGMENT OF TERMS AT CHECKOUT</h3>
                  <p className="mb-2">These Terms of Service are presented to you prior to completing any Purchase. By completing a Purchase, you confirm that:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>You have had the opportunity to read these Terms in full</li>
                    <li>You understand and accept all provisions contained herein</li>
                    <li>You acknowledge the nature of digital products as described in Section 3</li>
                    <li>You understand and accept the Refund Policy as described in Section 7</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* 3. NATURE OF DIGITAL PRODUCTS */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <AlertCircle className="w-8 h-8 text-yellow-400" />
                <h2 className="text-3xl font-bold">3. Nature of Digital Products</h2>
              </div>
              <div className="text-gray-300 leading-relaxed space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-purple-400 mb-3">3.1 IMMEDIATE DELIVERY AND ACCESS</h3>
                  <p className="mb-2">You acknowledge and agree that:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>All Courses are digital products delivered electronically</li>
                    <li>Upon completion of Purchase, you receive IMMEDIATE and PERMANENT access to all purchased Course Materials</li>
                    <li>Course Materials are available for immediate viewing, downloading, and/or streaming from the moment access is granted</li>
                    <li>The Company cannot retrieve, revoke, or "take back" any content you have downloaded, copied, viewed, or otherwise accessed</li>
                    <li>Due to the instant-access nature of digital products, traditional return policies applicable to physical goods do not apply</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-blue-400 mb-3">3.2 DOWNLOADABLE CONTENT</h3>
                  <p className="mb-2">Many Course Materials are available for download. You understand that:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Downloaded content remains on your devices permanently</li>
                    <li>The Company has no ability to delete, remove, or modify content that exists on your personal devices</li>
                    <li>Once downloaded, the value of the Course has been fully transferred to you</li>
                    <li>The availability of downloadable content is a benefit provided to enhance your learning experience and does not create any obligation for the Company to provide refunds</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-cyan-400 mb-3">3.3 COURSE STRUCTURE AND LEARNING METHODOLOGY</h3>
                  <p className="mb-2">Our Courses are structured with a specific pedagogical approach:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Foundational concepts and fundamentals are taught first</li>
                    <li>Advanced techniques, practical applications, and "power features" build upon these fundamentals</li>
                    <li>The full value of the Course is realized through complete engagement with all materials in the intended sequence</li>
                    <li>Partial completion or selective viewing does not constitute a valid basis for refund requests</li>
                    <li>The learning methodology is designed for optimal knowledge retention and skill development, which requires complete participation</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-green-400 mb-3">3.4 NO PHYSICAL GOODS</h3>
                  <p className="mb-2">Unless explicitly stated otherwise:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>No physical products, books, or materials will be shipped</li>
                    <li>All content is delivered digitally through the Platform</li>
                    <li>You are responsible for having compatible devices and internet access to view Course Materials</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* 4. ACCOUNT REGISTRATION AND SECURITY */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Shield className="w-8 h-8 text-purple-400" />
                <h2 className="text-3xl font-bold">4. Account Registration and Security</h2>
              </div>
              <div className="text-gray-300 leading-relaxed space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-purple-400 mb-3">4.1 ACCOUNT CREATION</h3>
                  <p className="mb-2">To access purchased Courses, you must create an account by providing:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>A valid email address</li>
                    <li>A secure password</li>
                    <li>Any additional information required during registration</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-blue-400 mb-3">4.2 ACCOUNT RESPONSIBILITY</h3>
                  <p className="mb-2">You are solely responsible for:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Maintaining the confidentiality of your login credentials</li>
                    <li>All activities that occur under your account</li>
                    <li>Immediately notifying us of any unauthorized access or security breach</li>
                    <li>Ensuring your account information remains accurate and current</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-cyan-400 mb-3">4.3 ONE USER PER LICENSE</h3>
                  <p className="mb-2">Each Purchase grants a license for a SINGLE USER only:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Account sharing is strictly prohibited</li>
                    <li>Login credentials may not be shared with others</li>
                    <li>Simultaneous access from multiple devices or locations may be monitored and restricted</li>
                    <li>Violation of this provision may result in immediate account termination without refund</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-green-400 mb-3">4.4 ACCOUNT SECURITY MEASURES</h3>
                  <p className="mb-2">We reserve the right to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Implement security measures including CAPTCHAs, two-factor authentication, or IP monitoring</li>
                    <li>Suspend accounts showing suspicious activity</li>
                    <li>Require identity verification for account recovery</li>
                    <li>Limit login attempts to prevent unauthorized access</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* 5. PURCHASE AND PAYMENT TERMS */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Scale className="w-8 h-8 text-green-400" />
                <h2 className="text-3xl font-bold">5. Purchase and Payment Terms</h2>
              </div>
              <div className="text-gray-300 leading-relaxed space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-purple-400 mb-3">5.1 PRICING</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>All prices are listed in United States Dollars (USD) unless otherwise specified</li>
                    <li>Prices are subject to change without notice, but changes will not affect completed Purchases</li>
                    <li>Promotional pricing, discounts, and bundle offers are time-limited and may be modified or discontinued at any time</li>
                    <li>Tax, if applicable, will be calculated and displayed at checkout</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-blue-400 mb-3">5.2 PAYMENT PROCESSING</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Payments are processed through third-party payment processors</li>
                    <li>You agree to the terms and privacy policies of these payment processors</li>
                    <li>The Company does not store complete credit card numbers</li>
                    <li>All payment information is transmitted using industry-standard encryption</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-cyan-400 mb-3">5.3 PAYMENT COMPLETION</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Your Purchase is complete when payment is successfully processed</li>
                    <li>Access to Course Materials is granted immediately upon payment confirmation</li>
                    <li>A receipt will be sent to your registered email address</li>
                    <li>Payment failures may delay or prevent access until resolved</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-green-400 mb-3">5.4 CHARGEBACKS AND PAYMENT DISPUTES</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Filing a chargeback or payment dispute without first contacting us through our official support channels is a breach of these Terms</li>
                    <li>Fraudulent chargebacks may result in account termination, debt collection, and legal action</li>
                    <li>We reserve the right to dispute any chargeback with full documentation of Course access and usage</li>
                    <li>Users who initiate chargebacks may be permanently banned from future Purchases</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-yellow-400 mb-3">5.5 CURRENCY AND INTERNATIONAL PURCHASES</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Currency conversion fees are the responsibility of the User</li>
                    <li>The Company is not responsible for exchange rate fluctuations</li>
                    <li>International purchases are subject to these same Terms</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* 6. LICENSE AND ACCESS RIGHTS */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <FileText className="w-8 h-8 text-cyan-400" />
                <h2 className="text-3xl font-bold">6. License and Access Rights</h2>
              </div>
              <div className="text-gray-300 leading-relaxed space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-purple-400 mb-3">6.1 LICENSE GRANT</h3>
                  <p className="mb-2">Upon completion of Purchase, the Company grants you a limited, non-exclusive, non-transferable, revocable license to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Access and view the purchased Course Materials for personal, non-commercial educational purposes</li>
                    <li>Download Course Materials designated as downloadable for personal use only</li>
                    <li>Take notes and create personal study materials based on Course content</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-red-400 mb-3">6.2 LICENSE RESTRICTIONS</h3>
                  <p className="mb-2">This license expressly DOES NOT permit you to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Copy, reproduce, or duplicate Course Materials for distribution to others</li>
                    <li>Share, sell, resell, license, or sublicense Course access or materials</li>
                    <li>Publish, broadcast, or publicly display Course Materials</li>
                    <li>Create derivative works based on Course Materials for commercial purposes</li>
                    <li>Use Course Materials to create competing products or services</li>
                    <li>Remove, alter, or obscure any copyright notices or proprietary markings</li>
                    <li>Use automated tools, bots, or scrapers to access or download content</li>
                    <li>Circumvent any technological protection measures</li>
                    <li>Share login credentials or allow others to access your account</li>
                    <li>Use Course Materials for any unlawful purpose</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-blue-400 mb-3">6.3 ACCESS DURATION</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Standard Purchases grant lifetime access to purchased Course Materials, subject to continued operation of the Platform</li>
                    <li>"Lifetime access" means access for as long as the Platform remains operational and you maintain a valid account in good standing</li>
                    <li>The Company reserves the right to discontinue the Platform with reasonable notice, during which time downloadable materials will remain available</li>
                    <li>Access may be revoked for violation of these Terms</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-cyan-400 mb-3">6.4 COURSE UPDATES</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>The Company may update Course content at its discretion</li>
                    <li>Updated content will be made available to existing purchasers when applicable</li>
                    <li>The Company is not obligated to update Courses or notify of updates</li>
                    <li>Major revisions may be offered as separate products</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* 7. REFUND POLICY - CRITICAL SECTION */}
            <div className="mb-12">
              <div className="bg-yellow-600/10 border border-yellow-500/30 rounded-xl p-6 mb-6">
                <h3 className="text-2xl font-bold text-yellow-400 mb-3">⚠️ PLEASE READ THIS SECTION CAREFULLY</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  This Refund Policy is designed to balance consumer protection with the unique nature of digital products that provide immediate, permanent access and downloadable content.
                </p>
              </div>

              <div className="flex items-center space-x-3 mb-6">
                <AlertCircle className="w-8 h-8 text-red-400" />
                <h2 className="text-3xl font-bold">7. Refund Policy</h2>
              </div>

              <div className="text-gray-300 leading-relaxed space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-purple-400 mb-3">7.1 GENERAL REFUND POLICY</h3>
                  <p className="mb-2">Due to the nature of digital products as described in Section 3:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">ALL SALES ARE GENERALLY FINAL</strong> upon completion of Purchase</li>
                    <li>You receive immediate, permanent access to Course Materials upon Purchase</li>
                    <li>Course Materials may be downloaded instantly upon gaining access</li>
                    <li>The Company cannot retrieve, revoke, or recover digital content once accessed or downloaded</li>
                    <li>Refunds are provided ONLY under the specific circumstances outlined below</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-blue-400 mb-3">7.2 REFUND ELIGIBILITY REQUIREMENTS</h3>
                  <p className="mb-3 font-semibold text-white">To be eligible for refund consideration, you MUST satisfy ALL of the following requirements:</p>

                  <div className="space-y-4">
                    <div className="bg-black/30 rounded-lg p-4 border border-white/10">
                      <h4 className="font-semibold text-cyan-400 mb-2">(a) COMPLETE COURSE COMPLETION</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                        <li>You must have completed 100% of all video lessons</li>
                        <li>You must have completed all quizzes and assessments</li>
                        <li>Completion must be verified through Platform analytics</li>
                      </ul>
                    </div>

                    <div className="bg-black/30 rounded-lg p-4 border border-white/10">
                      <h4 className="font-semibold text-cyan-400 mb-2">(b) FULL STUDY TIME INVESTMENT</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                        <li>You must have invested the recommended study time for the Course (as specified in Course description or materials)</li>
                        <li>Time investment must be demonstrable through Platform usage data</li>
                        <li>Rushing through content without genuine study effort does not constitute completion</li>
                      </ul>
                    </div>

                    <div className="bg-black/30 rounded-lg p-4 border border-white/10">
                      <h4 className="font-semibold text-cyan-400 mb-2">(c) COMPLETION OF ALL PRACTICE EXERCISES</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                        <li>You must have completed all practice examples, exercises, and hands-on activities included in the Course</li>
                        <li>Incomplete or skipped exercises disqualify refund eligibility</li>
                      </ul>
                    </div>

                    <div className="bg-black/30 rounded-lg p-4 border border-white/10">
                      <h4 className="font-semibold text-cyan-400 mb-2">(d) DOCUMENTED EFFORT EVIDENCE</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                        <li>You must provide documented evidence of your genuine effort to apply Course teachings</li>
                        <li>Acceptable documentation includes: exported conversation logs from AI/LLM platforms showing practice attempts, screenshots of completed exercises, evidence of prompt engineering attempts, project files demonstrating application of learned skills, and other verifiable proof of genuine effort</li>
                      </ul>
                    </div>

                    <div className="bg-black/30 rounded-lg p-4 border border-white/10">
                      <h4 className="font-semibold text-cyan-400 mb-2">(e) TIMELY REQUEST</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                        <li>Refund requests must be submitted within thirty (30) days of Purchase date</li>
                        <li>Requests submitted after 30 days are automatically ineligible</li>
                      </ul>
                    </div>

                    <div className="bg-black/30 rounded-lg p-4 border border-white/10">
                      <h4 className="font-semibold text-cyan-400 mb-2">(f) PROPER SUBMISSION</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                        <li>Refund requests must be submitted through official channels (see Section 19)</li>
                        <li>Requests must include all required documentation</li>
                        <li>Incomplete requests will not be processed</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-green-400 mb-3">7.3 REFUND REQUEST PROCESS</h3>
                  <p className="mb-2">If you believe you meet all eligibility requirements:</p>
                  <div className="bg-black/30 rounded-lg p-4 border border-white/10 space-y-2">
                    <p><strong className="text-white">Step 1:</strong> Email support@ai-mastery-hub.com with subject line "Refund Request - [Order Number]"</p>
                    <p><strong className="text-white">Step 2:</strong> Include in your request: your full name and account email, order number and date of Purchase, specific Course(s) for which you are requesting a refund, and detailed explanation of why the Course did not meet your expectations AFTER completing all materials and exercises</p>
                    <p><strong className="text-white">Step 3:</strong> Attach all required documentation: exported LLM/AI conversation logs showing practice attempts, screenshots of completed exercises, and any other evidence of genuine effort</p>
                    <p><strong className="text-white">Step 4:</strong> Allow up to fourteen (14) business days for review</p>
                    <p><strong className="text-white">Step 5:</strong> You will receive a written response regarding your request</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-yellow-400 mb-3">7.4 REFUND DETERMINATION</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>All refund requests are reviewed at the sole discretion of the Company</li>
                    <li>We will verify completion status, time investment, and submitted documentation</li>
                    <li>Approval or denial of refund requests is final</li>
                    <li>If approved, refunds will be processed to the original payment method within 7-14 business days</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-red-400 mb-3">7.5 NON-REFUNDABLE CIRCUMSTANCES</h3>
                  <p className="mb-3 font-semibold text-white">Refunds will NOT be provided under ANY of the following circumstances:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-sm">
                    <li><strong className="text-white">INCOMPLETE COURSE COMPLETION</strong> - You have not completed all Course materials in full; you skipped sections, lessons, or modules; you did not complete recommended exercises</li>
                    <li><strong className="text-white">INSUFFICIENT EFFORT</strong> - You did not invest the recommended study time; you cannot provide documentation of genuine learning efforts; you did not complete practice exercises and examples</li>
                    <li><strong className="text-white">CHANGE OF MIND</strong> - You simply changed your mind about the Purchase; the Course is "not what you expected" without completing it; you no longer have time or interest in the subject matter</li>
                    <li><strong className="text-white">BUYER'S REMORSE</strong> - You regret the Purchase for any reason; you found similar content elsewhere for free or cheaper; financial circumstances changed after Purchase</li>
                    <li><strong className="text-white">TECHNICAL ISSUES ON YOUR END</strong> - Your device does not meet technical requirements; you have internet connectivity issues; you cannot access content due to your own technical limitations</li>
                    <li><strong className="text-white">DOWNLOADED CONTENT</strong> - You have downloaded any Course Materials (in which case the full value has been received and cannot be returned)</li>
                    <li><strong className="text-white">THIRD-PARTY TOOL CHANGES</strong> - AI tools, APIs, or platforms referenced in Courses change their features, pricing, or availability; external services become unavailable; third-party terms of service are modified</li>
                    <li><strong className="text-white">DISSATISFACTION WITH RESULTS</strong> - You did not achieve expected income or business results; skills learned did not lead to desired outcomes; the Course did not guarantee any specific results</li>
                    <li><strong className="text-white">LATE REQUESTS</strong> - Request submitted more than 30 days after Purchase</li>
                    <li><strong className="text-white">BUNDLE OR DISCOUNTED PURCHASES</strong> - Bundles purchased at discount pricing are non-refundable; promotional or sale-priced items are non-refundable</li>
                    <li><strong className="text-white">POLICY VIOLATIONS</strong> - Your account was terminated for Terms violations; you engaged in prohibited uses of Course Materials</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-purple-400 mb-3">7.6 PARTIAL REFUNDS</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>The Company does not offer partial refunds</li>
                    <li>For bundle purchases, refunds (if approved) apply to the entire bundle only</li>
                    <li>Individual Course extraction from bundles is not available</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-blue-400 mb-3">7.7 RATIONALE FOR REFUND POLICY</h3>
                  <p className="mb-2">This policy exists because:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Digital products provide instant, permanent value upon access</li>
                    <li>Downloaded content cannot be retrieved or returned</li>
                    <li>The Courses are structured to deliver full value through complete engagement</li>
                    <li>Requiring course completion ensures Users have genuinely evaluated the product before claiming dissatisfaction</li>
                    <li>Documentation requirements prevent fraudulent refund requests by those who benefited from the content</li>
                    <li>This policy enables the Company to maintain sustainable operations while offering high-quality educational content</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-cyan-400 mb-3">7.8 CONSUMER PROTECTION COMPLIANCE</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>These Terms comply with applicable consumer protection laws</li>
                    <li>Nothing in these Terms affects your statutory rights that cannot be excluded by law</li>
                    <li>If you are in a jurisdiction that requires specific refund rights for digital products, those rights apply to the extent required by law</li>
                    <li>EU consumers acknowledge that by accessing Course Materials immediately upon Purchase, they consent to waive the 14-day withdrawal right under the Consumer Rights Directive</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* 8-18: Additional Sections - Abbreviated for readability */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <Shield className="w-8 h-8 text-purple-400" />
                <h2 className="text-3xl font-bold">8. Intellectual Property Rights</h2>
              </div>
              <div className="text-gray-300 leading-relaxed space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-purple-400 mb-3">8.1 COMPANY OWNERSHIP</h3>
                  <p className="mb-2">The Company retains all right, title, and interest in:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>All Course Materials and content</li>
                    <li>The Platform and its design, functionality, and code</li>
                    <li>All trademarks, logos, and brand elements</li>
                    <li>All proprietary methodologies, frameworks, and teaching approaches</li>
                    <li>All copyrights in original content</li>
                    <li>Any patents, trade secrets, or other intellectual property</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-blue-400 mb-3">8.2 COPYRIGHT PROTECTION</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>All Course Materials are protected by copyright law</li>
                    <li>Unauthorized reproduction, distribution, or public display is prohibited</li>
                    <li>The Company actively monitors for copyright infringement</li>
                    <li>Infringers will be pursued to the fullest extent of the law</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-cyan-400 mb-3">8.3 TRADEMARK NOTICE</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>AI Mastery Hub and associated logos are trademarks of the Company</li>
                    <li>Use of these marks without written permission is prohibited</li>
                    <li>Course names and titles may also be protected marks</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-green-400 mb-3">8.4 USER ACKNOWLEDGMENT</h3>
                  <p className="mb-2">By using the Platform, you acknowledge that:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>You acquire no ownership rights in any Course Materials</li>
                    <li>Your license is limited as described in Section 6</li>
                    <li>Violation of intellectual property rights may result in legal action and monetary damages</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Continue with remaining sections in abbreviated form */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-4 text-purple-400">9. Prohibited Uses</h2>
                <p className="text-gray-300 text-sm">
                  You agree NOT to: copy, reproduce, or duplicate Course Materials beyond personal learning; distribute, share, or transfer Course Materials to any third party; sell, resell, or commercially exploit Course Materials; use automated tools, bots, scrapers, or crawlers to access the Platform; share login credentials with others; file fraudulent chargebacks or payment disputes; use Course Materials for any unlawful purpose; or engage in any conduct that violates these Terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4 text-blue-400">10. User Content and Conduct</h2>
                <p className="text-gray-300 text-sm">
                  If the Platform allows you to submit content (such as comments, reviews, or community posts), you retain ownership of your original content but grant the Company a non-exclusive, royalty-free, worldwide license to use, display, and distribute your content in connection with the Platform. You represent that your content does not violate any third-party rights or laws. The Company may remove any content at its discretion.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4 text-yellow-400">11. Disclaimers and Limitation of Liability</h2>
                <p className="text-gray-300 text-sm mb-3">
                  <strong className="text-white">THE PLATFORM AND ALL COURSE MATERIALS ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.</strong> The Company expressly disclaims any guarantee that you will achieve specific income, financial, or business results. Educational content is provided for informational purposes. Individual results depend on numerous factors including effort, prior experience, market conditions, and proper application of concepts.
                </p>
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</strong> The Company shall not be liable for any indirect, incidental, special, consequential, or punitive damages; any loss of profits, revenue, data, or business opportunities. The Company's total liability for any claim shall not exceed the amount you paid for the specific Course at issue.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4 text-green-400">12. Indemnification</h2>
                <p className="text-gray-300 text-sm">
                  You agree to indemnify, defend, and hold harmless the Company and its owners, operators, employees, agents, and affiliates from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from: your violation of these Terms; your violation of any applicable law or third-party rights; your use or misuse of Course Materials; any content you submit or post; your negligent or wrongful conduct; or any dispute between you and any third party.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4 text-cyan-400">13. Third-Party Services and Tools</h2>
                <p className="text-gray-300 text-sm">
                  The Courses may reference, recommend, or utilize various third-party tools and services including AI language models (ChatGPT, Claude, etc.), API services, software applications, and online platforms. The Company is not affiliated with third-party service providers unless explicitly stated. You are responsible for obtaining your own access to third-party tools and must comply with third-party terms of service. Third-party tools may change features, pricing, or availability - such changes do not constitute grounds for refunds.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4 text-purple-400">14. Modifications to Terms and Content</h2>
                <p className="text-gray-300 text-sm">
                  The Company reserves the right to modify these Terms at any time. Material changes will be posted on the Platform. Your continued use after changes constitutes acceptance. The Company may update, modify, or remove Course content at any time. The "Last Updated" date at the top of these Terms indicates the most recent revision.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4 text-red-400">15. Termination</h2>
                <p className="text-gray-300 text-sm">
                  The Company may terminate or suspend your account and access immediately, without prior notice or liability, for any reason including breach of these Terms, fraudulent activity, violation of intellectual property rights, or abusive behavior. Upon termination, your license to access Course Materials is immediately revoked, you must cease all use of Course Materials, and no refund will be provided for termination due to Terms violation.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4 text-blue-400">16. Dispute Resolution and Arbitration</h2>
                <p className="text-gray-300 text-sm mb-3">
                  Before initiating formal dispute resolution, you agree to contact the Company and attempt to resolve any dispute informally for at least thirty (30) days. If informal resolution fails, any dispute, claim, or controversy arising from these Terms or your use of the Platform shall be resolved through binding arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules.
                </p>
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">CLASS ACTION WAIVER:</strong> YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION. YOU WAIVE ANY RIGHT TO PARTICIPATE IN CLASS ACTIONS OR CLASS-WIDE ARBITRATION.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4 text-green-400">17. Governing Law and Jurisdiction</h2>
                <p className="text-gray-300 text-sm">
                  These Terms shall be governed by and construed in accordance with the laws of the State of Tennessee, United States, without regard to conflict of law principles. For any matters not subject to arbitration, you consent to the exclusive jurisdiction of the state and federal courts located in the State of Tennessee, United States.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4 text-yellow-400">18. General Provisions</h2>
                <p className="text-gray-300 text-sm">
                  These Terms, together with any policies or guidelines referenced herein, constitute the entire agreement between you and the Company regarding the Platform and Courses. If any provision is found invalid or unenforceable, that provision shall be modified to the minimum extent necessary, and the remaining provisions shall continue in full force. You may not assign or transfer your rights under these Terms without written consent. The Company may assign these Terms freely in connection with a merger, acquisition, or sale of assets.
                </p>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Contact Information */}
            <div className="mb-12">
              <div className="flex items-center space-x-3 mb-6">
                <FileText className="w-8 h-8 text-purple-400" />
                <h2 className="text-3xl font-bold">19. Contact Information</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">
                For questions, concerns, or refund requests, please contact:
              </p>

              <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-xl p-6">
                <h4 className="font-semibold text-white mb-4">AI Mastery Hub</h4>
                <div className="space-y-2 text-gray-300">
                  <p><strong className="text-white">Operated by:</strong> Worldview Industries LLC</p>
                  <p><strong className="text-white">Email:</strong> <a href="mailto:support@ai-mastery-hub.com" className="text-purple-400 hover:text-purple-300">support@ai-mastery-hub.com</a></p>
                  <p><strong className="text-white">Mailing Address:</strong> 5750 Lake Resort Dr, Chattanooga, TN 37415</p>
                  <p className="mt-4 text-sm"><strong className="text-white">For refund requests, use subject line:</strong> "Refund Request - [Your Order Number]"</p>
                  <p className="text-sm mt-2"><strong className="text-white">Response times:</strong></p>
                  <ul className="list-disc list-inside ml-4 text-sm space-y-1">
                    <li>General inquiries: 3-5 business days</li>
                    <li>Refund requests: Up to 14 business days</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 my-8"></div>

            {/* Final Acknowledgment */}
            <div className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/30 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">ACKNOWLEDGMENT</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                BY COMPLETING A PURCHASE, YOU ACKNOWLEDGE THAT:
              </p>
              <ul className="space-y-2 text-gray-300">
                <li>1. You have read and understand these Terms of Service in their entirety.</li>
                <li>2. You understand the nature of digital products and that you receive immediate, permanent access upon Purchase.</li>
                <li>3. You understand and accept the Refund Policy, including all eligibility requirements.</li>
                <li>4. You agree to be bound by all Terms contained herein.</li>
                <li>5. You are entering into a legally binding agreement with the Company.</li>
              </ul>
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-gray-400 text-sm">
                  <strong className="text-white">AI Mastery Hub</strong><br />
                  Operated by Worldview Industries LLC<br />
                  Terms of Service v1.0<br />
                  © 2025 AI Mastery Hub. All Rights Reserved.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-4">
                AI Mastery Hub
              </h3>
              <p className="text-gray-400 text-sm">
                Empowering learners worldwide with cutting-edge digital education.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Courses</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/courses" className="hover:text-white transition">All Courses</a></li>
                <li><a href="/#" className="hover:text-white transition">AI & Machine Learning</a></li>
                <li><a href="/#" className="hover:text-white transition">Prompt Engineering</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/#" className="hover:text-white transition">About Us</a></li>
                <li><a href="/contact" className="hover:text-white transition">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/privacy-policy" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="/terms-of-service" className="hover:text-white transition">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-400">© 2025 AI Mastery Hub. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white transition">Twitter</a>
              <a href="#" className="text-gray-400 hover:text-white transition">LinkedIn</a>
              <a href="#" className="text-gray-400 hover:text-white transition">YouTube</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
